import React, { useState, Suspense } from 'react';
import PropTypes from 'prop-types';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { SubmitContainer, TopSubmit, BottomSubmit } from '../CreateForm.styles';
import {
  httpCreateCourse,
  httpCreateAtt,
  httpCreatePdf,
} from '../../../Hooks/admin/admin.post.hooks';
const PrimaryButton = React.lazy(
  () =>
    import('../../../Components/Buttons/PrimaryButton/PrimaryButton.component')
);
const Loader = React.lazy(
  () => import('../../../Components/Loader/LoaderComponent.component')
);
import { compressPDF } from '../../../TrainingRoutes/Certificates/Certificate/compressPDF';

const CreateCourseSubmit = ({
  questions = [],
  course,
  pdf = {},
  videoLinks = [],
  att = [],
  image = {},
  tags = [],
  setSuccessState,
  setIsOpen,
  disabled,
  successState,
}) => {
  // httpCreatePdf(formDataA, course.courseName)
  const [loading, setLoading] = useState(false);
  const [successMain, setSuccessMain] = useState('');
  const [successPdf, setSuccessPdf] = useState('');
  const [successAtt, setSuccessAtt] = useState('');
  const queryClient = useQueryClient();
  const mainDataMutation = useMutation({
    mutationFn: (fullCourse) => httpCreateCourse(fullCourse),
    onSuccess: async (data) => {
      let message = await data.json();
      queryClient.invalidateQueries(['courses']);
      setSuccessMain(message.msg || 'Course has been created');
    },
    onError: async (error) => {
      let err = await error.json();
      console.error('Error creating course:', error);
      setSuccessMain(err.msg || 'Failed to create course');
    },
  });
  const pdfDataMutation = useMutation({
    mutationFn: ({ formData, courseName }) =>
      httpCreatePdf(formData, courseName),
    onSuccess: async (data) => {
      let message = await data.json();
      queryClient.invalidateQueries(['courses']);
      setSuccessPdf(message.msg || 'PDF data has been created');
    },
    onError: async (error) => {
      let err = await error.json();
      console.error('Error creating course pdf:', error);
      setSuccessPdf(err.msg || 'Failed to create course pdf ');
    },
  });

  const attDataMutation = useMutation({
    mutationFn: ({ formData, courseName }) =>
      httpCreateAtt(formData, courseName),
    onSuccess: async (data) => {
      let message = await data.json();
      queryClient.invalidateQueries(['courses']);
      setSuccessAtt(message.msg || 'Course has been created');
    },
    onError: async (error) => {
      let err = await error.json();
      console.error('Error creating course:', error);
      setSuccessAtt(err.msg || 'Failed to create course');
    },
  });

  const removeEmptyStrings = (obj) => {
    return Object.fromEntries(
      Object.entries(obj).filter(([_, v]) => v !== '' || _ === '')
    );
  };

  const mappedQuestions = questions.map((question) => {
    return removeEmptyStrings(question);
  });

  const handleSubmit = async (e) => {
    try {
      e.preventDefault();
      setLoading(true);
      setSuccessMain('Uploading main data...');
      const fullCourse = {
        ...course,
        tags: tags,
        videoLinks: videoLinks,
        quiz: mappedQuestions,
        image,
        pdf: '',
        attachments: [],
        markedForDeletion: null,
        setForDelete: false,
      };

      // Create the course
      const mainDataPromise = mainDataMutation.mutateAsync(fullCourse);

      const promises = [mainDataPromise]; // Array to hold all promises

      // Handle PDF upload
      if (pdf) {
        setSuccessPdf('Uploading PDF...');
        const formDataA = new FormData();
        const pdfFile = new File([pdf], pdf.name.replace(/\s+/g, '_'), {
          type: pdf.type,
        });
        const compressedBlob = await compressPDF(pdfFile);
        const compressedPDF = new File(
          [compressedBlob],
          pdf.name.replace(/\s+/g, '_'),
          { type: 'application/pdf' }
        );
        formDataA.append('pdf', compressedPDF);

        const pdfPromise = pdfDataMutation.mutateAsync({
          formData: formDataA,
          courseName: course.courseName,
        });

        promises.push(pdfPromise); // Add the PDF upload promise to the array
      }

      // Handle attachments upload
      if (att && att.length > 0) {
        setSuccessAtt('Uploading attachments...');
        const formDataC = new FormData();
        att.forEach((file, index) => {
          const attachmentFile = new File(
            [file],
            file.name.replace(/\s+/g, '_'),
            { type: file.type }
          );
          formDataC.append(`attachment_${index}`, attachmentFile);
        });
        const attPromise = attDataMutation.mutateAsync({
          formData: formDataC,
          courseName: course.courseName,
        });

        promises.push(attPromise);
      }
      await Promise.all(promises);
    } catch (err) {
      console.error('Error creating course:', err);
      setSuccessState('An error has occurred creating the course');
    } finally {
      setSuccessState('Process has been completed');
      setIsOpen(true);
      setLoading(false);
    }
  };

  return (
    <SubmitContainer>
      <TopSubmit>
        {successMain.length > 0 && (
          <p className="result">Main Data: {successMain}</p>
        )}
        {successPdf.length > 0 && <p className="result">PDF: {successPdf}</p>}
        {successAtt.length > 0 && (
          <p className="result">Attachments: {successAtt}</p>
        )}
        {successState.length > 0 && (
          <p className="result">Result: {successState}</p>
        )}
      </TopSubmit>
      <Suspense fallback={<p>Loading components...</p>}>
        {loading ? (
          <TopSubmit>
            <Loader />
          </TopSubmit>
        ) : (
          <BottomSubmit>
            <PrimaryButton
              functionName={(e) => handleSubmit(e)}
              span="Create Course"
              disabled={!disabled}
            />
          </BottomSubmit>
        )}
      </Suspense>
    </SubmitContainer>
  );
};

CreateCourseSubmit.propTypes = {
  questions: PropTypes.array,
  pdf: PropTypes.object,
  course: PropTypes.object,
  videoLinks: PropTypes.array,
  att: PropTypes.array,
  image: PropTypes.string,
  tags: PropTypes.array,
  setSuccessState: PropTypes.func,
  setIsOpen: PropTypes.func,
  disabled: PropTypes.bool,
  successState: PropTypes.string,
};

export default CreateCourseSubmit;
